import { createContext, useCallback, useMemo, useState } from 'react';
import { useToast } from '@pw-tech/omni-ui';
import { useUpdateRegistrationDetails } from '@/modules/RegisterLayout/hooks/useRegistrationDetails';
import { useRouter } from 'next/router';
import { useLeadCapture } from '../hooks/useLeadCapture';
type RegistrationContextType = {
  registrationDetails: any;
  setRegistrationDetails: React.Dispatch<React.SetStateAction<any>>;
  isFormSubmitting: boolean;
  setIsFormSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  submitRegisterForm: (data: any, pushRoute?: boolean) => Promise<any>;
};
const registrationState = {
  registrationDetails: null,
  setRegistrationDetails: () => null,
  isFormSubmitting: false,
  setIsFormSubmitting: () => null,
  submitRegisterForm: (data: any, pushRoute?: boolean) => Promise.resolve()
};
export const RegistrationContext = createContext<RegistrationContextType>(registrationState);
const RegistrationContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    mutateAsync: asyncLeadCapture
  } = useLeadCapture();
  const [registrationDetails, setRegistrationDetails] = useState<RegistrationContextType['registrationDetails']>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const router = useRouter();
  const {
    toast: showToaster
  } = useToast();
  const {
    mutate: updateRegistrationDetails
  } = useUpdateRegistrationDetails();
  const submitRegisterForm = useCallback(async (data: any, pushRoute?: boolean) => {
    setIsFormSubmitting(true);
    return new Promise((resolve, reject) => {
      updateRegistrationDetails({
        ...data
      }, {
        onSuccess: async (response: any) => {
          if (response?.status === 200 || response?.statusCode === 200) {
            setRegistrationDetails(response?.data);
            asyncLeadCapture();
            if (pushRoute) {
              router.push({
                pathname: '/register',
                query: {
                  courseId: response?.data?.courseId || data?.courseId,
                  step: response?.data?.step || 1
                }
              });
            }
            resolve(response);
          }
        },
        onError: (error: any) => {
          showToaster({
            message: typeof error === 'string' ? error : 'Error in updating registration details',
            variant: 'error',
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center'
            }
          });
          reject(error);
        },
        onSettled: () => {
          setIsFormSubmitting(false);
        }
      });
    });
  }, [updateRegistrationDetails, router, showToaster]);
  const userStore = useMemo(() => {
    return {
      registrationDetails,
      setRegistrationDetails,
      isFormSubmitting,
      setIsFormSubmitting,
      submitRegisterForm
    };
  }, [registrationDetails, isFormSubmitting, setIsFormSubmitting, submitRegisterForm]);
  return <RegistrationContext.Provider value={userStore} data-sentry-element="unknown" data-sentry-component="RegistrationContextProvider" data-sentry-source-file="RegistrationContextProvider.tsx">{children}</RegistrationContext.Provider>;
};
export default RegistrationContextProvider;